/**
 * @file SpaceSideField.tsx
 * @description Одно поле отступа секции числом (phase19.16, партия SB120,
 * этап 1 «секция-герой»; site-builder.md SB120, SB100; проект
 * uiux-sb120-stage1 §2.2):
 *
 * ```
 * Внутри сверху                         ↺ как у сайта
 * ├────────────●─────────┤ [ 96 ] px
 *  [0] [S 16] [M 32] [L 64] [XL 96]
 *  на телефоне 29 px (30 %)                    Своё ›
 * ```
 *
 * ПК — `look.space.<сторона>` (нет ключа — «как у сайта», ступени — шкала
 * сайта × плотность магазина); телефон — `look.space.phone.<сторона>` (нет
 * ключа — ПК × доля, «↺ по доле»). `sides` из двух — пара «⛓ одинаково»:
 * правка пишет обе стороны. Дробные px не округляются (30,72 после перевода
 * ступеней при «Компактно»). Курсор над полем — подсветка на канве,
 * перетаскивание — живой предпросмотр.
 */

import type { SectionLook, SectionSpace, SectionSpaceSide } from "@chaiberi/shared";

import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { DeviceValueRow } from "../fields/DeviceValueRow";
import { LookWarningNote } from "../fields/LookWarningNote";
import { useSectionForm } from "../section-form-context";
import {
  formatPx,
  phoneFactorOf,
  phoneSpacePx,
  setPhoneSide,
  setSpaceSide,
  siteSpacePx,
  sideRange,
  spacePresets,
  SPACE_SIDE_HIGHLIGHT,
  type SpaceTheme,
} from "../../../../lib/site-builder/look-space";

/** Пиксели без округления показа: 30,72 остаётся 30,72. */
const PX_DISPLAY = { factor: 1, decimals: 2 } as const;

interface Props {
  /** Одна сторона или пара слева/справа (одинаково). */
  sides: readonly [SectionSpaceSide] | readonly [SectionSpaceSide, SectionSpaceSide];
  label: string;
  look: SectionLook;
  theme: SpaceTheme;
  onChange: (space: SectionSpace) => void;
  /** Подсказка под полем («как у сайта: 22 · 40 · 70 по ширине экрана»). */
  hint?: string;
}

export function SpaceSideField({ sides, label, look, theme, onChange, hint }: Props) {
  const { preview, highlight } = useSectionForm();
  const [first] = sides;
  const space = look.space;
  const range = sideRange(first);
  const site = siteSpacePx(first, look, theme.density);
  const factor = phoneFactorOf(space, theme);
  const phone = phoneSpacePx(first, look, theme);
  const desktopPx = space[first] ?? site;
  const write = (value: number | undefined, phoneSide: boolean) =>
    onChange(sides.reduce<SectionSpace>((acc, side) => (phoneSide ? setPhoneSide(acc, side, value) : setSpaceSide(acc, side, value)), space));
  const send = (value: number | undefined, prefix: string) => sides.forEach((side) => preview(`${prefix}${side}`, value));
  const summary = phone.own ? `на телефоне своё: ${formatPx(phone.value)} px` : `на телефоне ${formatPx(phone.value)} px (${Math.round(factor * 100)} %)`;
  const paths = sides.flatMap((side) => [`look.space.${side}`, `look.space.phone.${side}`]);

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <NumberSliderField
        label={label}
        value={space[first]}
        inherit={{ value: site, label: "как у сайта" }}
        min={range.min}
        max={range.max}
        step={1}
        largeStep={8}
        unit="px"
        display={PX_DISPLAY}
        presets={spacePresets(theme.density)}
        hint={hint}
        onChange={(v) => write(v, false)}
        onPreview={(v) => send(v, "look.space.")}
        onHover={(inside) => highlight(inside ? SPACE_SIDE_HIGHLIGHT[first] : null)}
      />
      <DeviceValueRow summary={summary} own={phone.own} label={`${label} на телефоне`}>
        <NumberSliderField
          label={`${label} на телефоне`}
          value={space.phone?.[first]}
          inherit={{ value: Math.round(desktopPx * factor * 100) / 100, label: "по доле" }}
          min={range.min}
          max={range.max}
          step={1}
          unit="px"
          display={PX_DISPLAY}
          onChange={(v) => write(v, true)}
          onPreview={(v) => send(v, "look.space.phone.")}
        />
      </DeviceValueRow>
      <LookWarningNote paths={paths} />
    </div>
  );
}
